import React, { useCallback, useState, useMemo, useEffect, useContext } from 'react';
import { IEditorProps } from './Editor';
import {
  IPropertyChild,
  getChildValueAsArray,
  getEditorHeightStyle,
  getEditorWrapperHeightStyle,
} from 'components/property-editors/utils/ProviderNodeUtils';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { Checkbox } from '../inputs/Checkbox';
import { SearchField } from '../inputs/SearchField';
import classNames from 'classnames';
import './CheckListEditor.less';
import { useEditorId } from './hooks/useEditorId';
import { EditorLabel } from './EditorLabel';
import { EditorValidationWrapper } from './EditorValidationWrapper';
import { EditorConfigButtons } from './EditorConfigButtons';
import { EditorConfigButton } from './EditorConfigButton';
import { ListBoxEmptyMessage } from 'components/lists/ListBoxEmptyMessage';
import { submitStringArrayValue } from '@decisions/server/DecisionsFramework/Design/Properties/PropertyEditorService';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { AwaitableContext } from 'contexts/AwaitableContext';

interface ICheckListEditorProps extends IEditorProps {}

const getCheckContainerClass = (searchShowing?: boolean) =>
  classNames('dpc-checklist-editor__checkboxes', {
    'dpc-checklist-editor__checkboxes--search-showing': searchShowing,
  });

/**
 * Simple Check List Editor.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function CheckListEditor({ child, path, onChange }: ICheckListEditorProps) {
  const { editingContextId } = useContext(PropertyEditorContext);
  const { addPromise, removePromise } = useContext(AwaitableContext);
  const [searchString, setSearchString] = useState('');
  const [id] = useEditorId(path);
  const [selected, setSelected] = useState<string[]>(getChildValueAsArray(child));
  const options = useMemo(() => getCheckOptions(child), [child]);
  const noValueMessage: string = getEditorConfigValue(child, 'noValuesMessage') || 'No Data';

  useEffect(() => {
    setSelected(getChildValueAsArray(child));
  }, [child]);

  const changeHandler = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      let selectedElements = [...selected];
      if (selected.includes(value)) {
        selectedElements.splice(selected.indexOf(value), 1);
      } else {
        selectedElements.push(value);
      }

      setSelected(selectedElements);

      const id = path.join(',');
      addPromise(
        id,
        (async () => {
          try {
            await submitStringArrayValue({ editingContext: editingContextId, path, selectedElements });
            removePromise(id);
          } catch (error) {
            console.error('Error saving checklist value', error);
          }
        })()
      );
    },
    [addPromise, editingContextId, path, removePromise, selected]
  );

  const onSearch = useCallback((search: string) => {
    setSearchString(search);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchString('');
  }, [setSearchString]);

  const onToggleAll = useCallback(() => {
    const newValue = selected.length === options.length ? [] : [...options];
    setSelected(newValue);
    onChange(newValue, path);
  }, [onChange, options, path, selected.length]);

  const wrapperHeight = getEditorWrapperHeightStyle(child);
  const editorHeightStyles = getEditorHeightStyle(child);

  return (
    <EditorLabel
      htmlFor={id}
      editor={child}
      path={path}
      className="dpc-checklist-editor"
      doNotWrap
      height={wrapperHeight}
    >
      {addSearch(child) && (
        <SearchField onSearch={onSearch} name={'Search'} value={searchString} onClear={clearSearch} />
      )}
      <EditorValidationWrapper editor={child}>
        <div className={getCheckContainerClass(addSearch(child))} style={{ ...editorHeightStyles }}>
          {options
            .filter(value => value.toUpperCase().includes(searchString.toUpperCase()))
            .map(value => (
              <Checkbox
                key={`${id}-${value}`}
                id={`${id}-${value}`}
                label={value}
                value={value}
                name={child.Node.Name}
                onChange={changeHandler}
                checked={selected.includes(value)}
                grouped
              />
            ))}
          <ListBoxEmptyMessage list={options} text={noValueMessage} className="dpc-checklist-editor__placeholder" />
        </div>
      </EditorValidationWrapper>
      {options.length > 1 && (
        <EditorConfigButtons>
          <EditorConfigButton onClick={onToggleAll}>Select/Deselect All</EditorConfigButton>
        </EditorConfigButtons>
      )}
    </EditorLabel>
  );
}

function getCheckOptions(child: IPropertyChild): string[] {
  return (getEditorConfigValue(child, 'values') || []).filter((value?: string | null) => {
    if (!value) {
      console.log('Improper Configuration: falsy value passed as Checklist entry name');
      return false;
    }
    return true;
  });
}

function addSearch(child: IPropertyChild) {
  return getCheckOptions(child).length > 30;
}
